import type { CSSProperties, ReactNode } from "react";

export type CardProps = {
  children: ReactNode;
  /** "armed" renders corner brackets — used for selected/focused cards in the design system */
  armed?: boolean;
  /** tight reduces internal padding from 16px to 12px */
  tight?: boolean;
  /** flat removes the elevation (use for sub-cards inside other cards) */
  flat?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
};

/**
 * Card — base container. The "armed" variant adds corner brackets (the
 * design system's signature flourish), used on selected/focused cards.
 */
export function Card({
  children,
  armed = false,
  tight = false,
  flat = false,
  className,
  style,
  onClick,
}: CardProps) {
  const interactive = !!onClick;
  return (
    <div
      className={[
        "bht-card",
        armed && "bht-card--armed",
        tight && "bht-card--tight",
        flat && "bht-card--flat",
        interactive && "bht-card--interactive",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onClick?.();
            }
          : undefined
      }
    >
      {armed && (
        <div className="bht-card__brackets" aria-hidden>
          <span /><span /><span /><span />
        </div>
      )}
      {children}
    </div>
  );
}
